import { LocalizableMessage } from '@sparelabs/translate'
import { ApiError, IMetadataObject, LocalizeFunction } from './ErrorTypes'

// Common metadata interface for OpenFleetError-based classes
export interface IOpenFleetErrorMetadata<T = unknown> extends IMetadataObject {
  message?: string
  code?: string
  providerStatus?: number
  providerResponse?: T
}

export enum FailureSource {
  OpenFleetProvider = 'openFleetProvider',
  OpenFleetProviderService = 'openFleetProviderService',
  OpenFleetLimit = 'openFleetLimit',
  OpenFleetServiceValidation = 'openFleetServiceValidation',
  DispatchValidation = 'dispatchValidation',
  Unknown = 'unknown',
}

/**
 * This enum is used to indicate the source of a failure.
 * - Provider: The provider's (e.g. Uber) API returned an error.
 * - ProviderService: The implementation service (e.g. uber-service) returned an error,
 *    often some validation implemented in the provider service. E.g Uber rejects requests with accessibility features.
 * - FleetLimit: An Open Fleet limit was violated by the request.
 * - OpenFleetServiceValidation: Some form of validation failed in open-fleet-service, for example
 *    a request was made with the same configuration twice.
 * - DispatchValidation: Dispatch validation failed via DispatchManager.ts in `sparelabs-api`
 */

export interface IOpenFleetFailureMetadata extends IMetadataObject {
  failureSource?: FailureSource
  failureMessage?: string
}

/**
 * This is the super-case for all errors that are thrown by OpenFleet implementations.
 * Please use the subclasses below to indicate the specific error type.
 */
export class OpenFleetError<T = unknown> extends ApiError<T> {
  constructor(
    status: number,
    messageInput: string | LocalizeFunction<T>,
    localizeOptions?: T,
    public readonly metadata: IOpenFleetErrorMetadata = {},
    public readonly serializeMetadata: boolean = true,
  ) {
    super(status, messageInput, metadata, localizeOptions)
  }
}

/**
 * This error is thrown when the OpenFleet implementation is unable to provide
 * an estimate for a trip due to any failure source.
 */
export class OpenFleetNoEstimateError extends OpenFleetError {
  public static status = 400

  constructor(localizableMessage: LocalizableMessage, metadata: IOpenFleetFailureMetadata = {}) {
    super(OpenFleetNoEstimateError.status, (_options, locale) => localizableMessage(locale), undefined, metadata)
  }
}

/**
 * This error is thrown when the open fleet is unable to fulfill a request due to
 * any failure source.
 */
export class OpenFleetNoRequestError<T = unknown> extends OpenFleetError<T> {
  public static status = 400

  constructor(
    messageInput: string | LocalizeFunction<T>,
    metadata: IOpenFleetFailureMetadata = {},
    localizeOptions?: T,
  ) {
    super(OpenFleetNoRequestError.status, messageInput, localizeOptions, metadata)
  }
}
/**
 * This error is thrown when the provider (e.g. Uber) does not respond in time
 * or returns 5xx errors. This error has a 400-level status code to avoid tripping
 * alerts that call pagerduty and interfere with the CI/CD pipeline.
 */
export class OpenFleetProviderError<T = undefined> extends OpenFleetError<T> {
  public static status = 424

  constructor(messageInput: string | LocalizeFunction<T>, metadata: IOpenFleetErrorMetadata = {}) {
    super(OpenFleetProviderError.status, messageInput, undefined, metadata)
  }
}
/**
 * This error is thrown when 4xx errors are returned by the provider (e.g. Uber)
 * or from configuration issues.
 */
export class OpenFleetImplementationError<T = undefined> extends OpenFleetError<T> {
  public static status = 500

  constructor(
    messageInput: string | LocalizeFunction<T>,
    metadata: IOpenFleetErrorMetadata = {},
    status: number = OpenFleetImplementationError.status,
  ) {
    super(status, messageInput, undefined, metadata)
  }
}

/**
 * This error is thrown when the OpenFleet implementation is unable to provide
 * an estimate or fulfill a request due to a limit violation.
 */
export class OpenFleetRateLimitError extends OpenFleetImplementationError {
  public static status = 429

  constructor(localizableMessage: LocalizableMessage, metadata: IOpenFleetErrorMetadata = {}) {
    super((_options, locale) => localizableMessage(locale), metadata, OpenFleetRateLimitError.status)
  }
}

/**
 * This error is thrown when the provider (e.g. Uber) returns a 404 error.
 * This is a special case under OpenFleetImplementationError, that should only
 * be used for providers where we don't want to treat 404s as 500s.
 */
export class OpenFleetNotFoundError extends OpenFleetError<undefined> {
  public static status = 404

  constructor(localizableMessage: LocalizableMessage, metadata: IOpenFleetErrorMetadata = {}) {
    super(OpenFleetNotFoundError.status, (_options, locale) => localizableMessage(locale), undefined, metadata)
  }
}
export interface IOpenFleetLimitErrorMetadata<L = unknown, R = unknown> extends IMetadataObject {
  type: string
  limit?: L
  requested?: R
}

/**
 * This error is thrown when the OpenFleet implementation is unable to provide
 * an estimate or fulfill a request due to a limit violation.
 */
export class OpenFleetLimitViolationError extends OpenFleetError<undefined> {
  public static status = 400

  constructor(localizableMessage: LocalizableMessage, metadata: IOpenFleetLimitErrorMetadata) {
    super(OpenFleetLimitViolationError.status, (_options, locale) => localizableMessage(locale), undefined, metadata)
  }
}
